import ApiError from "shared/components/feedback/modals/ErrorModal";
import { GenericError } from "shared/constants/errorTypes";
import GenericModal from "shared/components/feedback/modals/common/GenericModal";
import ReactDOM from "react-dom";
import { useCallback } from "react";

// Configuración unificada de errores
const ERROR_CONFIG = {
    // Errores HTTP 4XX
    400: {
        title: "Bad Request",
        subTitle: "Invalid Request",
        message: "We couldn't process your request",
        actionMessage: "Please check your URL, or reload the page to start again",
        showDetail: false,
        redirect: false
    },
    401: {
        title: "Unauthorized",
        subTitle: "Authentication Required",
        message: "You are no longer authorized to connect to some of the services",
        actionMessage: "Contact support team",
        showDetail: true,
        redirect: false
    },
    403: {
        title: "Access Denied",
        subTitle: "Forbidden",
        message: "You don't have permission to access this resource",
        actionMessage: "Please contact support team",
        showDetail: true,
        redirect: false
    },
    404: {
        title: "Not Found",
        subTitle: "Resource Not Available",
        message: "Some of the services that the page required are unavailable",
        actionMessage: "Please contact support team",
        showDetail: false,
        redirect: false
    },
    408: {
        title: "Request Timed Out",
        subTitle: "Server Response Timeout",
        message: "We were unable to process your request. The services took too long to respond",
        actionMessage: "If the problem persists please contact support team",
        showDetail: true,
        redirect: false
    },

    // Errores HTTP 5XX
    500: {
        title: "Internal Server Error",
        subTitle: "Server Error",
        message: "Something failed in the application. Please try again.",
        actionMessage: "If the problem persists please email support and add a description of what you were doing when the error occured or contact support team",
        showDetail: true,
        redirect: false
    },
    501: {
        title: "Not Implemented",
        subTitle: "Feature Not Available",
        message: "This feature is not currently implemented. Please try again later.",
        actionMessage: "If the problem persists please email support and add a description of what you were doing when the error occured or contact support team",
        showDetail: true,
        redirect: false
    },
    502: {
        title: "Bad Gateway",
        subTitle: "Server Communication Error",
        message: "The server received an invalid response. Please try again.",
        actionMessage: "If the problem persists please email support and add a description of what you were doing when the error occured or contact support team",
        showDetail: true,
        redirect: false
    },
    503: {
        title: "Server is Unavailable",
        subTitle: "Service Temporarily Unavailable",
        message: "The server is temporarily unavailable. Please try again in a few minutes",
        actionMessage: "If the problem persists please email support and add a description of what you were doing when the error occured or contact support team",
        showDetail: true,
        redirect: false
    },
    504: {
        title: "Gateway Timeout",
        subTitle: "Server Response Timeout",
        message: "The server took too long to respond. Please try again.",
        actionMessage: "If the problem persists please email support and add a description of what you were doing when the error occured or contact support team",
        showDetail: true,
        redirect: false
    },

    // Errores de red/cliente (códigos custom)
    NETWORK_ERROR: {
        match: ['network', 'failed to fetch', 'networkerror'],
        title: "Network Error",
        subTitle: "Unable to reach server",
        message: "There is a network issue",
        actionMessage: "Please check your internet connection and try again",
        showDetail: false,
        redirect: false
    },
    ABORT_ERROR: {
        match: ['abort'],
        title: "Request Cancelled",
        subTitle: "Request was aborted",
        message: "The request was cancelled",
        actionMessage: "Please try again if this was not intentional",
        showDetail: false,
        redirect: false
    },
    TIMEOUT_ERROR: {
        match: ['timeout'],
        title: "Timeout Error",
        subTitle: "Request took too long",
        message: "The services took too long to respond",
        actionMessage: "Please try again. If the problem persists, contact support team",
        showDetail: false,
        redirect: false
    },
    CORS_ERROR: {
        match: ['cors', 'cross-origin'],
        title: "CORS Error",
        subTitle: "Cross-origin request blocked",
        message: "Cross-origin request was blocked",
        actionMessage: "Please contact support team",
        showDetail: false,
        redirect: false
    },
    UNKNOWN_ERROR: {
        title: "Network Error",
        subTitle: "Unable to reach server",
        message: "An unknown network error occurred",
        actionMessage: "Please try again. If the problem persists, contact support team",
        showDetail: false,
        redirect: false
    },
    DEFAULT_ERROR: {
        title: "Unknown error",
        subTitle: "Something went wrong",
        message: "An unknown error occured. Please try again.",
        actionMessage: "If the problem persists please email support and add a description of what you were doing when the error occured or contact support team",
        showDetail: false,
        redirect: false
    }
};

export const useErrors = () => {

    const mountModal = useCallback((content) => {
        ReactDOM.render(content, document.getElementById("divAppErrorModal"));
    }, []);

    const removeError = useCallback(() => {
        mountModal(null);
    }, [mountModal]);

    const addError = useCallback((error) => {
        const modal =
            <GenericModal
                content={<ApiError error={error} popup />}
                parent="divAppErrorModal"
                closable={error.recoverable}
                width={530}
            />;

        mountModal(modal);
    }, [mountModal]);

    const buildGenericError = useCallback((error, config, recoverable = true, showDetail = true) => {
        if (error instanceof GenericError) {
            return error;
        }

        return new GenericError(config.message, {
            title: config.title,
            subTitle: config.subTitle,
            actionMessage: config.actionMessage,
            rawError: error?.rawError || error?.stack || error?.ex,
            status: error?.status,
            correlationId: error?.correlationId,
            date: error?.date || Date.now(),
            action: removeError,
            showDetail: showDetail,
            recoverable: recoverable,
            detail: error?.message,
        });
    }, [removeError]);

    const handleUnknownError = useCallback((error) => {
        const errorMessage = error?.message?.toLowerCase() || '';

        // Search for matches in network/client errors
        const networkErrors = [
            'NETWORK_ERROR',
            'ABORT_ERROR',
            'TIMEOUT_ERROR',
            'CORS_ERROR'
        ];

        let config = ERROR_CONFIG.UNKNOWN_ERROR;

        for (const errorKey of networkErrors) {
            const errorConfig = ERROR_CONFIG[errorKey];
            if (errorConfig.match?.some(keyword => errorMessage.includes(keyword))) {
                config = errorConfig;
                break;
            }
        }

        const errorBuild = buildGenericError(error, config);

        addError(errorBuild);

    }, [addError, buildGenericError]);

    const handleErrorDefault = useCallback((error) => {

        const errorBuild = buildGenericError(error, ERROR_CONFIG.DEFAULT_ERROR);
        addError(errorBuild);

    }, [addError, buildGenericError]);

    const handleError = useCallback((error) => {
        // We have 3 kinds of error handling here:

        // 1 -
        // this is for network errors that do not have status codes
        // this is probably because we could not reach the backend for some reason
        if (!error?.status) {
            handleUnknownError(error);
            return;
        }

        // 2 -
        // We handle 4XX and 5XX errors here.
        const config = ERROR_CONFIG[error.status];

        if (config) {
            const errorBuild = buildGenericError(error, config);
            addError(errorBuild);
            return;
        }

        // 3-
        // Otherwise, use the default error handler, since we don't know much about this issue
        handleErrorDefault(error);

    }, [addError, buildGenericError, handleUnknownError, handleErrorDefault]);

    return { handleError, removeError, addError };
};
